import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import { useLanguage } from "../context/LanguageContext";
import { usePageMeta } from "../utils/usePageMeta";

const TermsPage = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();

    // Legal pages exist in Ukrainian only, so they get a canonical but no hreflang pair
    usePageMeta({
        title: "Договір оренди інструменту — SmarTOOL",
        description: "Публічна оферта SmarTOOL: умови оренди електроінструменту у Харкові, тарифи, права та обов'язки сторін.",
        canonical: "https://kh-tools.com/terms",
        lang: "uk",
    });

    return (
        <div className="min-h-screen bg-gray-50">
            <Header
                title={t("terms.title")}
                showBack
                onBack={() => navigate(-1)}
            />

            <div className="p-4 pb-8 animate-slide-in">
                {/* Title */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h1 className="text-base font-bold text-gray-900 text-center leading-snug">
                        ДОГОВІР ПУБЛІЧНОЇ ОФЕРТИ ПРО НАДАННЯ ПОСЛУГ З ОРЕНДИ
                        ІНСТРУМЕНТУ ТА ВИКОРИСТАННЯ РЕКУРЕНТНИХ ПЛАТЕЖІВ
                    </h1>
                    <p className="text-sm text-gray-600 mt-3 leading-relaxed">
                        Цей Договір є публічною офертою (пропозицією) компанії
                        ФОП Ляшенко Є.С. (далі — «Орендодавець») спрямованою
                        невизначеному колу осіб (далі — «Орендар») про надання
                        послуг з прокату інструментів через автоматизовані
                        пункти видачі (автомати).
                    </p>
                </div>

                {/* Section 1 */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            1
                        </span>
                        ПРЕДМЕТ ДОГОВОРУ
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <p>
                            <span className="font-medium text-gray-700">
                                1.1.
                            </span>{" "}
                            Орендодавець передає, а Орендар приймає в тимчасове
                            оплатне користування інструмент(и), вибраний(-і)
                            через інтерфейс автомата (далі — «Інструмент»).
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                1.2.
                            </span>{" "}
                            Орендар підтверджує, що прийняв Інструмент у
                            справному стані, без зовнішніх пошкоджень, повністю
                            укомплектованим та ознайомленим з правилами його
                            експлуатації.
                        </p>
                    </div>
                </div>

                {/* Section 2 */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            2
                        </span>
                        ПРАВА ТА ОБОВ'ЯЗКИ ОРЕНДАРЯ
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <p>
                            <span className="font-medium text-gray-700">
                                2.1.
                            </span>{" "}
                            Орендар зобов'язується використовувати Інструмент
                            суворо за призначенням, дотримуючись правил техніки
                            безпеки.
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                2.2.
                            </span>{" "}
                            Орендар зобов'язується повернути Інструмент у
                            зазначений у договорі (інтерфейсі автомата) час, у
                            чистому та справному стані, в тій же комплектації, в
                            якій він був отриманий.
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                2.3.
                            </span>{" "}
                            Орендар несе повну матеріальну відповідальність за
                            збереження Інструменту з моменту його отримання з
                            автомата до моменту повернення в автомат.
                        </p>
                    </div>
                </div>

                {/* Section 3 */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            3
                        </span>
                        ВІДПОВІДАЛЬНІСТЬ ОРЕНДАРЯ ТА РЕКУРЕНТНІ ПЛАТЕЖІ
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <p>
                            <span className="font-medium text-gray-700">
                                3.1.
                            </span>{" "}
                            Орендар надає свою безумовну згоду на використання
                            механізму рекурентних платежів (автоматичного
                            списання коштів без необхідності додаткового
                            підтвердження кожного списання) зі своєї банківської
                            картки, дані якої були використані для оплати
                            оренди.
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                3.2.
                            </span>{" "}
                            Списання неустойки та збитків: Орендодавець має
                            право в односторонньому порядку списати кошти з
                            картки Орендаря у разі порушення умов договору:
                        </p>

                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-2 space-y-2">
                            <p className="font-medium text-gray-700">
                                Затримка повернення (до 3-х днів):
                            </p>
                            <p>
                                За кожен день прострочення (навіть неповний) понад встановлений час оренди, Орендар сплачує пеню у розмірі вартості однієї доби оренди (відповідно до тарифу, зазначеного в автоматі при отриманні) за кожен день затримки. Нарахування пені здійснюється до моменту повернення Орендарем повністю справного інструменту або повної компенсації його ринкової вартості.
                            </p>

                            <p className="font-medium text-gray-700 pt-1">
                                Пошкодження Інструменту:
                            </p>
                            <p>
                                Вартість ремонту, запчастин та робіт сервісного
                                центру.
                            </p>

                            <p className="font-medium text-gray-700 pt-1">
                                Знищення / Втрата Інструменту (від 3 днів):
                            </p>
                            <p>
                                Повна ринкова вартість нового аналогічного
                                Інструменту.
                            </p>
                        </div>

                        <p className="mt-2">
                            <span className="font-medium text-gray-700">
                                3.3.
                            </span>{" "}
                            Якщо на момент списання коштів на картці Орендаря
                            недостатньо коштів, Орендодавець має право
                            здійснювати повторні спроби списання до повного
                            погашення заборгованості.
                        </p>
                    </div>
                </div>

                {/* Section 4 */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            4
                        </span>
                        ПРИСВОЄННЯ МАЙНА
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <p>
                            <span className="font-medium text-gray-700">
                                4.1.
                            </span>{" "}
                            Якщо Інструмент не буде повернутий Орендарем
                            протягом 3 (трьох) календарних днів після закінчення
                            терміну оренди, зазначеного при отриманні, такий
                            Інструмент вважається привласненим (втраченим)
                            Орендарем.
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                4.2.
                            </span>{" "}
                            У цьому випадку, крім оплати штрафних санкцій за час
                            прострочення (п. 3.2), Орендар зобов'язується
                            сплатити повну ринкову вартість Інструменту, яка
                            буде автоматично списана з його банківської картки.
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                4.3.
                            </span>{" "}
                            Орендодавець залишає за собою право звернутися до
                            правоохоронних органів із заявою про привласнення
                            майна (ст. 191 КК України) та вжити заходів для
                            примусового стганення боргу.
                        </p>
                    </div>
                </div>

                {/* Section 5 - NEW: Персональні дані */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            5
                        </span>
                        ОБРОБКА ПЕРСОНАЛЬНИХ ДАНИХ
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <p>
                            <span className="font-medium text-gray-700">
                                5.1.
                            </span>{" "}
                            Орендар надає згоду на обробку своїх персональних
                            даних (пакет пасспортних данних, номер телефону, дані банківської картки,
                            зображення з камер відеоспостереження автомата)
                            Орендодавцем.
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                5.2.
                            </span>{" "}
                            Метою обробки даних є забезпечення виконання цього
                            Договору, ідентифікація Орендаря, запобігання
                            шахрайству та захист майна Орендодавця.
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                5.3.
                            </span>{" "}
                            Детальні умови обробки, зберігання та захисту
                            персональних даних визначені в{" "}
                            <Link
                                to="/privacy"
                                className="text-blue-600 underline"
                            >
                                Політиці конфіденційності
                            </Link>
                            , яка є невід'ємною частиною цього Договору.
                        </p>
                    </div>
                </div>

                {/* Section 6 - Updated */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            6
                        </span>
                        ПРИЙНЯТТЯ УМОВ (АКЦЕПТ)
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <p>
                            <span className="font-medium text-gray-700">
                                6.1.
                            </span>{" "}
                            Натискання кнопки «Я приймаю умови договору оренди»
                            в інтерфейсі автомата та списання оплати за оренду
                            означає повне та беззастережне прийняття Орендарем
                            умов цього Договору, включаючи згоду на автоматичне
                            списання коштів (рекурентні платежі) та згоду на
                            обробку персональних даних згідно з{" "}
                            <Link
                                to="/privacy"
                                className="text-blue-600 underline"
                            >
                                Політикою конфіденційності
                            </Link>
                            .
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                6.2.
                            </span>{" "}
                            Орендар підтверджує, що ознайомився з{" "}
                            <Link
                                to="/safety"
                                className="text-blue-600 underline"
                            >
                                Правилами безпеки та експлуатації інструменту
                            </Link>
                            , які є невід'ємною частиною цього Договору, та
                            приймає на себе повний ризик і відповідальність за
                            наслідки використання Інструменту, включаючи
                            відповідальність перед третіми особами.
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                6.3.
                            </span>{" "}
                            Факт оплати та автоматичного відкриття комірки
                            локера є моментом підписання{" "}
                            <Link
                                to="/agreement"
                                className="text-blue-600 underline"
                            >
                                Акту прийому-передачі обладнання
                            </Link>{" "}
                            електронним підписом Орендаря. Цей Акт підтверджує
                            передачу Інструменту у робочому стані та фіксує
                            зобов'язання щодо його повернення та перевірки
                            протягом 10 хвилин.
                        </p>
                    </div>
                </div>

                {/* Safety link */}
                <Link
                    to="/safety"
                    className="block bg-white rounded-2xl shadow-sm p-5 mb-4 active:scale-[0.98] transition-transform"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-5 h-5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                </svg>
                            </span>
                            <span className="text-sm font-bold text-gray-900">
                                {t("terms.safetyButton")}
                            </span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 text-gray-400"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </div>
                </Link>
            </div>
        </div>
    );
};

export default TermsPage;
